import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  onSnapshot,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'staff';
const ASSIGNED_COLLECTION_NAME = 'assigned_staff';

// Simple cache for faster subsequent loads
let staffCache = null;
let lastCacheTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

// Get all staff members
export const getStaff = async () => {
  try {
    // Check cache first
    const now = Date.now();
    if (staffCache && (now - lastCacheTime) < CACHE_DURATION) {
      console.log('Using cached staff data');
      return staffCache;
    }

    console.log('Getting staff from Firestore...');
    const q = query(collection(db, COLLECTION_NAME), orderBy('login'));
    const querySnapshot = await getDocs(q);
    const staff = [];
    querySnapshot.forEach((doc) => {
      staff.push({ id: doc.id, ...doc.data() });
    });
    
    // Update cache
    staffCache = staff;
    lastCacheTime = now;
    
    console.log('Retrieved staff:', staff);
    return staff;
  } catch (error) {
    console.error('Error getting staff:', error);
    throw error;
  }
};

// Test Firebase connection
export const testFirebaseConnection = async () => {
  try {
    console.log('Testing Firebase connection...');
    console.log('Database reference:', db);
    console.log('Collection name:', COLLECTION_NAME);
    
    // Test basic Firestore access
    const testQuery = query(collection(db, COLLECTION_NAME));
    console.log('Query created successfully');
    
    const querySnapshot = await getDocs(testQuery);
    console.log('Query executed successfully');
    console.log('Number of documents found:', querySnapshot.size);
    
    console.log('Firebase connection test successful');
    return true;
  } catch (error) {
    console.error('Firebase connection test failed:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    
    // Provide specific error information
    if (error.code === 'permission-denied') {
      console.error('PERMISSION DENIED: Check Firestore security rules');
    } else if (error.code === 'unavailable') {
      console.error('UNAVAILABLE: Check internet connection and Firebase project status');
    } else if (error.code === 'not-found') {
      console.error('NOT FOUND: Check if Firestore Database is enabled');
    }
    
    return false;
  }
};

// Test Firebase write permissions
export const testFirebaseWrite = async () => {
  try {
    console.log('Testing Firebase write permissions...');
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Write test timeout')), 5000);
    });
    
    const writePromise = (async () => {
      const testData = {
        test: true,
        timestamp: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), testData);
      console.log('Write test successful, document ID:', docRef.id);
      
      // Clean up test document
      await deleteDoc(docRef);
      console.log('Test document cleaned up');
      
      return true;
    })();
    
    return await Promise.race([writePromise, timeoutPromise]);
  } catch (error) {
    console.error('Firebase write test failed:', error);
    return false;
  }
};

// Listen to real-time updates
export const subscribeToStaff = (callback) => {
  console.log('Setting up real-time listener for staff...');
  
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('login'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log('Real-time update received:', querySnapshot.size, 'documents');
      const staff = [];
      querySnapshot.forEach((doc) => {
        staff.push({ id: doc.id, ...doc.data() });
      });
      
      // Update cache with new data
      staffCache = staff;
      lastCacheTime = Date.now();
      
      console.log('Updated staff data:', staff);
      callback(staff);
    }, (error) => {
      console.error('Error in real-time listener:', error);
      // Call callback with empty array to prevent infinite loading
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time listener:', error);
    // Return a no-op function and call callback with empty array
    callback([]);
    return () => {};
  }
};

// Add new staff member
export const addStaff = async (staffData) => {
  try {
    console.log('Adding staff member:', staffData);
    
    // Check for duplicate login before adding
    const existingStaff = await getStaff();
    const isDuplicate = existingStaff.some(staff => staff.login === staffData.login);
    
    if (isDuplicate) {
      throw new Error(`Staff member with login "${staffData.login}" already exists`);
    }
    
    console.log('No duplicates found, proceeding to add staff...');
    console.log('Collection name:', COLLECTION_NAME);
    console.log('Database reference:', db);
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), staffData);
    console.log('Staff member added with ID:', docRef.id);
    console.log('Document reference:', docRef);
    
    // Verify the document was actually created
    const verifyDoc = await getDocs(collection(db, COLLECTION_NAME));
    console.log('Total documents in collection after add:', verifyDoc.size);
    
    // Invalidate cache
    staffCache = null;
    
    return { id: docRef.id, ...staffData };
  } catch (error) {
    console.error('Error adding staff:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Update staff member
export const updateStaff = async (id, updates) => {
  try {
    console.log('Updating staff member:', id, updates);
    
    // If updating login, check for duplicates
    if (updates.login) {
      const existingStaff = await getStaff();
      const isDuplicate = existingStaff.some(staff => 
        staff.login === updates.login && staff.id !== id
      );
      
      if (isDuplicate) {
        throw new Error(`Staff member with login "${updates.login}" already exists`);
      }
    }
    
    const staffRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(staffRef, updates);
    console.log('Staff member updated successfully');
    
    // Invalidate cache
    staffCache = null;
    
    return { id, ...updates };
  } catch (error) {
    console.error('Error updating staff:', error);
    throw error;
  }
};

// Delete staff member
export const deleteStaff = async (id) => {
  try {
    console.log('Deleting staff member:', id);
    const staffRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(staffRef);
    console.log('Staff member deleted successfully');
    
    // Invalidate cache
    staffCache = null;
    
    return id;
  } catch (error) {
    console.error('Error deleting staff:', error);
    throw error;
  }
};

// Initialize with default data (run once) - REMOVED DEFAULT STAFF
export const initializeDefaultStaff = async () => {
  // This function is no longer needed since we don't initialize default staff
  console.log('No initialization needed - starting with empty staff list');
  return Promise.resolve();
}; 

// Get assigned staff
export const getAssignedStaff = async () => {
  try {
    console.log('Getting assigned staff...');
    const querySnapshot = await getDocs(collection(db, ASSIGNED_COLLECTION_NAME));
    const assignedStaff = {
      A01: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: [],
        eastRunner: [],
        eastVRC: []
      },
      A02: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A03: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A04: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      }
    };
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.floor && data.area && data.staff) {
        if (!assignedStaff[data.floor]) {
          assignedStaff[data.floor] = {
            westSideWaterspider: [],
            processGuidePick: [],
            pickAmbassador: []
          };
        }
        assignedStaff[data.floor][data.area] = data.staff || [];
      }
    });
    
    console.log('Assigned staff retrieved:', assignedStaff);
    return assignedStaff;
  } catch (error) {
    console.error('Error getting assigned staff:', error);
    return {
      A01: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: [],
        eastRunner: [],
        eastVRC: []
      },
      A02: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A03: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A04: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      }
    };
  }
};

// Subscribe to assigned staff changes
export const subscribeToAssignedStaff = (callback) => {
  console.log('Setting up real-time listener for assigned staff...');
  
  try {
    const q = query(collection(db, ASSIGNED_COLLECTION_NAME));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log('Real-time assigned staff update received:', querySnapshot.size, 'documents');
      const assignedStaff = {
        A01: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: [],
          eastRunner: [],
          eastVRC: []
        },
        A02: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        },
        A03: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        },
        A04: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        }
      };
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Document data:', doc.id, data);
        if (data.floor && data.area && data.staff) {
          if (!assignedStaff[data.floor]) {
            assignedStaff[data.floor] = {
              westSideWaterspider: [],
              processGuidePick: [],
              pickAmbassador: []
            };
          }
          assignedStaff[data.floor][data.area] = data.staff || [];
        }
      });
      
      console.log('Updated assigned staff data:', assignedStaff);
      callback(assignedStaff);
    }, (error) => {
      console.error('Error in assigned staff real-time listener:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Call callback with empty data on error
      callback({
        A01: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: [],
          eastRunner: [],
          eastVRC: []
        },
        A02: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        },
        A03: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        },
        A04: {
          westSideWaterspider: [],
          westToteRunner: [],
          westPA: [],
          eastSpider: [],
          eastPA: []
        }
      });
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up assigned staff real-time listener:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    // Call callback with empty data and return no-op function
    callback({
      A01: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: [],
        eastRunner: [],
        eastVRC: []
      },
      A02: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A03: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      },
      A04: {
        westSideWaterspider: [],
        westToteRunner: [],
        westPA: [],
        eastSpider: [],
        eastPA: []
      }
    });
    return () => {};
  }
};

// Update assigned staff for a specific area and floor
export const updateAssignedStaff = async (floor, area, staff) => {
  try {
    console.log('Updating assigned staff for floor:', floor, 'area:', area, 'staff:', staff);
    
    // Check if document exists for this floor and area
    const areaQuery = query(
      collection(db, ASSIGNED_COLLECTION_NAME), 
      where('floor', '==', floor),
      where('area', '==', area)
    );
    const querySnapshot = await getDocs(areaQuery);
    
    if (querySnapshot.empty) {
      // Create new document for this floor and area
      const docRef = await addDoc(collection(db, ASSIGNED_COLLECTION_NAME), {
        floor: floor,
        area: area,
        staff: staff,
        updatedAt: new Date()
      });
      console.log('Created new assigned staff document for floor:', floor, 'area:', area, 'with ID:', docRef.id);
    } else {
      // Update existing document
      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        staff: staff,
        updatedAt: new Date()
      });
      console.log('Updated existing assigned staff document for floor:', floor, 'area:', area, 'with ID:', docRef.id);
    }
    
    return true;
  } catch (error) {
    console.error('Error updating assigned staff:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Clear all assigned staff (for reset functionality)
export const clearAllAssignedStaff = async () => {
  try {
    console.log('Clearing all assigned staff...');
    const querySnapshot = await getDocs(collection(db, ASSIGNED_COLLECTION_NAME));
    
    const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    console.log('All assigned staff cleared');
    return true;
  } catch (error) {
    console.error('Error clearing assigned staff:', error);
    throw error;
  }
}; 

// Test assigned staff functionality
export const testAssignedStaffConnection = async () => {
  try {
    console.log('Testing assigned staff connection...');
    
    // Try to create a test document
    const testData = {
      area: 'test_area',
      staff: [],
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, ASSIGNED_COLLECTION_NAME), testData);
    console.log('Test assigned staff document created:', docRef.id);
    
    // Clean up test document
    await deleteDoc(docRef);
    console.log('Test assigned staff document cleaned up');
    
    return true;
  } catch (error) {
    console.error('Assigned staff connection test failed:', error);
    return false;
  }
}; 

// Delete a saved plan
export const deletePlan = async (planId) => {
  try {
    console.log('Deleting plan with ID:', planId);
    await deleteDoc(doc(db, 'saved_plans', planId));
    console.log('Plan deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting plan:', error);
    throw error;
  }
}; 